import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, downloadFile, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Printer, FileDown, FileSpreadsheet, FileText, Calendar, Download } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { formatUSD, formatDate, monthName } from "@/lib/format";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import ShareLinksManager from "@/components/ShareLinksManager";
import YoyComparison from "@/components/YoyComparison";
import CoreLogo from "@/components/CoreLogo";
import { useTheme } from "@/contexts/ThemeContext";

export default function TaxReportPage() {
  const { user } = useAuth();
  const { theme, brokerageName } = useTheme();
  const currentYear = new Date().getFullYear();
  const [mode, setMode] = useState("year"); // "year" | "range" | "yoy"
  const [year, setYear] = useState(currentYear);
  const [start, setStart] = useState(`${currentYear}-01-01`);
  const [end, setEnd] = useState(`${currentYear}-12-31`);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const params = useMemo(() => (
    mode === "year" ? { year } : { start, end }
  ), [mode, year, start, end]);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/report/summary", { params });
      setData(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, [params]);

  useEffect(() => {
    if (mode === "yoy") return;
    load();
    // Auto-refresh when tab regains focus — reflects deals just moved to Closed
    const onFocus = () => { if (!document.hidden && mode !== "yoy") load(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [load, mode]);

  const yearOptions = [];
  for (let y = currentYear + 1; y >= currentYear - 5; y--) yearOptions.push(y);

  const handleDownload = async (kind) => {
    const ext = kind;
    const fname = `tax-report-${mode === "year" ? year : `${start}_${end}`}.${kind}`;
    setBusy(kind);
    try {
      await downloadFile(`/report/${kind}`, params, fname);
      toast.success(`${kind.toUpperCase()} downloaded`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(null);
    }
    return ext;
  };

  if (mode !== "yoy" && !data) return <div className="p-10 text-sm text-neutral-500">Loading…</div>;

  const t = data?.totals || { transaction_count: 0, total_sales: 0, gross_commission: 0, broker_fees: 0, fmls_fees: 0, referral_fees: 0, transaction_fees: 0, net_commission: 0, total_expenses: 0, net_profit: 0 };
  const periodLabel = mode === "year"
    ? `Tax Year ${year}`
    : `${formatDate(start)} – ${formatDate(end)}`;

  return (
    <div className="px-6 lg:px-10 py-8 print:p-0" data-testid="tax-report-page">
      {/* Toolbar (hidden in print) */}
      <div className="print:hidden flex flex-col lg:flex-row gap-4 lg:items-end lg:justify-between mb-8">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">Year-End Records</div>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">Tax Report</h1>
          <p className="text-sm text-neutral-500 mt-2 max-w-xl">
            Profit &amp; loss for closed transactions and deductible expenses. Print or export below — keep a copy for your CPA.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {mode !== "yoy" && (
            <>
              <Button onClick={() => window.print()} variant="outline"
                className="rounded-none border-neutral-300 hover:bg-neutral-100 h-10" data-testid="print-button">
                <Printer className="h-4 w-4 mr-2" strokeWidth={1.5} />
                Print
              </Button>
              <Button onClick={() => handleDownload("pdf")} disabled={busy === "pdf"}
                className="rounded-none bg-neutral-900 hover:bg-neutral-800 text-white h-10 font-semibold px-5" data-testid="download-pdf-button">
                <Download className="h-4 w-4 mr-2" strokeWidth={2} />
                {busy === "pdf" ? "Preparing…" : "Download as PDF"}
              </Button>
              <Button onClick={() => handleDownload("excel")} disabled={busy === "excel"}
                variant="outline"
                className="rounded-none border-neutral-300 hover:bg-neutral-100 h-10" data-testid="download-excel-button">
                <FileSpreadsheet className="h-4 w-4 mr-2" strokeWidth={1.5} />
                {busy === "excel" ? "Preparing…" : "Excel"}
              </Button>
              <Button onClick={() => handleDownload("csv")} disabled={busy === "csv"}
                variant="outline" className="rounded-none border-neutral-300 hover:bg-neutral-100 h-10" data-testid="download-csv-button">
                <FileDown className="h-4 w-4 mr-2" strokeWidth={1.5} />
                {busy === "csv" ? "Preparing…" : "CSV"}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Period selector */}
      <div className="print:hidden border border-neutral-200 p-4 mb-8">
        <Tabs value={mode} onValueChange={setMode}>
          <TabsList className="rounded-none bg-neutral-100 p-0 h-auto">
            <TabsTrigger value="year" className="rounded-none data-[state=active]:bg-neutral-900 data-[state=active]:text-white px-4 py-2 font-mono text-xs uppercase tracking-[0.15em]" data-testid="period-tab-year">
              Calendar Year
            </TabsTrigger>
            <TabsTrigger value="range" className="rounded-none data-[state=active]:bg-neutral-900 data-[state=active]:text-white px-4 py-2 font-mono text-xs uppercase tracking-[0.15em]" data-testid="period-tab-range">
              Custom Range
            </TabsTrigger>
            <TabsTrigger value="yoy" className="rounded-none data-[state=active]:bg-neutral-900 data-[state=active]:text-white px-4 py-2 font-mono text-xs uppercase tracking-[0.15em]" data-testid="period-tab-yoy">
              Year-over-Year
            </TabsTrigger>
          </TabsList>

          <TabsContent value="year" className="mt-4">
            <div className="flex flex-wrap gap-2">
              {yearOptions.map((y) => (
                <button
                  key={y}
                  onClick={() => setYear(y)}
                  className={`px-4 py-2 text-sm font-mono border transition-colors ${
                    y === year ? "bg-neutral-900 text-white border-neutral-900" : "bg-white text-neutral-700 border-neutral-300 hover:bg-neutral-50"
                  }`}
                  data-testid={`report-year-${y}`}
                >
                  {y}
                </button>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="range" className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-xl">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 flex items-center gap-1">
                  <Calendar className="h-3 w-3" strokeWidth={1.5} /> Start date
                </Label>
                <Input type="date" value={start} onChange={(e) => setStart(e.target.value)}
                  className="mt-1 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900" data-testid="range-start-input" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 flex items-center gap-1">
                  <Calendar className="h-3 w-3" strokeWidth={1.5} /> End date
                </Label>
                <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
                  className="mt-1 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900" data-testid="range-end-input" />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="yoy" className="mt-4">
            <p className="text-sm text-neutral-500">Compare two tax years side by side, with deltas and a 12-month overlay.</p>
          </TabsContent>
        </Tabs>
      </div>

      {mode === "yoy" ? (
        <YoyComparison />
      ) : (
        <>

      {/* PRINTABLE DOCUMENT */}
      <div className="print-page bg-white border border-neutral-200 print:border-0 p-6 lg:p-10">
        {/* Document header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between border-b border-neutral-900 pb-4 mb-6">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-1">Schedule of Income &amp; Expenses</div>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight">End-of-Year Tax Report</h2>
            <div className="font-mono text-sm text-neutral-700 mt-2">{periodLabel}</div>
          </div>
          <div className="flex items-end gap-6 mt-4 sm:mt-0">
            <div className="text-right">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">Prepared for</div>
              <div className="font-serif text-xl text-neutral-900 mt-1">{user?.full_name}</div>
              <div className="font-mono text-xs text-neutral-500">
                {user?.brokerage || "—"} {user?.license_no ? `· Lic # ${user.license_no}` : ""}
              </div>
              <div className="font-mono text-[10px] text-neutral-400 mt-1">
                Generated {new Date().toISOString().slice(0, 10)}
              </div>
            </div>
            <div className="hidden sm:block text-neutral-900 border-l border-neutral-200 pl-6" data-testid="report-core-logo">
              <CoreLogo variant="mark" size={56} />
            </div>
          </div>
        </div>

        {/* Summary block */}
        <section className="mb-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">I. Profit &amp; Loss Summary</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-neutral-300">
            <SummaryRow label="Closed Transactions" value={t.transaction_count} mono testid="sum-deals" />
            <SummaryRow label="FMLS 0.12% Fee" value={formatUSD(t.fmls_fees || 0)} mono testid="sum-fmls" />
            <SummaryRow label="Total Sales Volume" value={formatUSD(t.total_sales)} mono testid="sum-sales" />
            <SummaryRow label="Broker Fees / Splits" value={formatUSD(t.broker_fees)} mono testid="sum-broker" />
            <SummaryRow label="Gross Commission" value={formatUSD(t.gross_commission)} mono testid="sum-gross" />
            <SummaryRow label="Referral Fees" value={formatUSD(t.referral_fees)} mono testid="sum-referral" />
            <SummaryRow label="Net Commission (Income)" value={formatUSD(t.net_commission)} mono emphasis testid="sum-net" />
            <SummaryRow label="Transaction Fees" value={formatUSD(t.transaction_fees)} mono testid="sum-txn" />
            <SummaryRow label="Total Expenses" value={formatUSD(t.total_expenses)} mono testid="sum-expenses" color="expense" />
          </div>
          <div className="border border-neutral-900 border-t-0 flex items-baseline justify-between px-5 py-4 bg-neutral-50">
            <div className="font-serif text-xl">Net Profit</div>
            <div
              className="font-mono text-3xl"
              style={{ color: t.net_profit >= 0 ? "var(--profit)" : "var(--expense)" }}
              data-testid="net-profit-value"
            >
              {formatUSD(t.net_profit)}
            </div>
          </div>
        </section>

        {/* Closed transactions table */}
        <section className="mb-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">II. Closed Transactions</div>
          <div className="overflow-x-auto border border-neutral-300">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-neutral-900 text-white">
                  <th className="text-left font-sans font-medium uppercase tracking-[0.12em] text-[10px] px-3 py-2">Date</th>
                  <th className="text-left font-sans font-medium uppercase tracking-[0.12em] text-[10px] px-3 py-2">Property</th>
                  <th className="text-left font-sans font-medium uppercase tracking-[0.12em] text-[10px] px-3 py-2">Client</th>
                  <th className="text-left font-sans font-medium uppercase tracking-[0.12em] text-[10px] px-3 py-2">Side</th>
                  <th className="text-right font-sans font-medium uppercase tracking-[0.12em] text-[10px] px-3 py-2">Sale</th>
                  <th className="text-right font-sans font-medium uppercase tracking-[0.12em] text-[10px] px-3 py-2">%</th>
                  <th className="text-right font-sans font-medium uppercase tracking-[0.12em] text-[10px] px-3 py-2">Gross</th>
                  <th className="text-right font-sans font-medium uppercase tracking-[0.12em] text-[10px] px-3 py-2">Broker Fees</th>
                  <th className="text-right font-sans font-medium uppercase tracking-[0.12em] text-[10px] px-3 py-2">Referral</th>
                  <th className="text-right font-sans font-medium uppercase tracking-[0.12em] text-[10px] px-3 py-2">Txn</th>
                  <th className="text-right font-sans font-medium uppercase tracking-[0.12em] text-[10px] px-3 py-2">Net</th>
                </tr>
              </thead>
              <tbody>
                {data.deals.length === 0 ? (
                  <tr><td colSpan={11} className="text-center text-neutral-500 py-8 text-sm" data-testid="no-deals-row">
                    No closed transactions in this period.
                  </td></tr>
                ) : data.deals.map((d) => (
                  <tr key={d.id} className="row-hover border-t border-neutral-200" data-testid={`report-deal-row-${d.id}`}>
                    <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{formatDate(d.close_date)}</td>
                    <td className="px-3 py-2">{d.property_name}</td>
                    <td className="px-3 py-2 text-neutral-600">{d.client_name || "—"}</td>
                    <td className="px-3 py-2 text-neutral-600 capitalize">{d.side}</td>
                    <td className="px-3 py-2 font-mono text-right">{formatUSD(d.sale_price)}</td>
                    <td className="px-3 py-2 font-mono text-right text-neutral-600">{Number(d.commission_rate).toFixed(2)}%</td>
                    <td className="px-3 py-2 font-mono text-right">{formatUSD(d.gross_commission)}</td>
                    <td className="px-3 py-2 font-mono text-right" style={{ color: "var(--expense)" }}>{formatUSD(d.broker_fees)}</td>
                    <td className="px-3 py-2 font-mono text-right" style={{ color: "var(--expense)" }}>{formatUSD(d.referral_fee)}</td>
                    <td className="px-3 py-2 font-mono text-right" style={{ color: "var(--expense)" }}>{formatUSD(d.transaction_fee)}</td>
                    <td className="px-3 py-2 font-mono text-right font-semibold">{formatUSD(d.net_commission)}</td>
                  </tr>
                ))}
              </tbody>
              {data.deals.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-neutral-900 bg-neutral-50 font-semibold">
                    <td className="px-3 py-2 font-mono text-[11px] uppercase tracking-[0.15em]" colSpan={4}>Totals</td>
                    <td className="px-3 py-2 font-mono text-right">{formatUSD(t.total_sales)}</td>
                    <td />
                    <td className="px-3 py-2 font-mono text-right">{formatUSD(t.gross_commission)}</td>
                    <td className="px-3 py-2 font-mono text-right" style={{ color: "var(--expense)" }}>{formatUSD(t.broker_fees)}</td>
                    <td className="px-3 py-2 font-mono text-right" style={{ color: "var(--expense)" }}>{formatUSD(t.referral_fees)}</td>
                    <td className="px-3 py-2 font-mono text-right" style={{ color: "var(--expense)" }}>{formatUSD(t.transaction_fees)}</td>
                    <td className="px-3 py-2 font-mono text-right">{formatUSD(t.net_commission)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </section>

        {/* Expenses by category */}
        <section className="mb-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">III. Deductible Expenses — By Category</div>
            <div className="border border-neutral-300">
              <table className="w-full text-sm">
                <thead className="bg-neutral-900 text-white">
                  <tr>
                    <th className="text-left font-sans font-medium uppercase tracking-[0.12em] text-[10px] px-3 py-2">Category</th>
                    <th className="text-right font-sans font-medium uppercase tracking-[0.12em] text-[10px] px-3 py-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.expenses_by_category.length === 0 ? (
                    <tr><td colSpan={2} className="text-center text-neutral-500 py-6">No expenses logged.</td></tr>
                  ) : data.expenses_by_category.map((row) => (
                    <tr key={row.category} className="border-t border-neutral-200" data-testid={`cat-row-${row.category}`}>
                      <td className="px-3 py-2">{row.category}</td>
                      <td className="px-3 py-2 font-mono text-right" style={{ color: "var(--expense)" }}>{formatUSD(row.amount)}</td>
                    </tr>
                  ))}
                </tbody>
                {data.expenses_by_category.length > 0 && (
                  <tfoot>
                    <tr className="border-t-2 border-neutral-900 bg-neutral-50">
                      <td className="px-3 py-2 font-mono text-[11px] uppercase tracking-[0.15em]">Total</td>
                      <td className="px-3 py-2 font-mono text-right font-semibold">{formatUSD(t.total_expenses)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          {/* Monthly chart (only screen, not print) */}
          <div className="print:hidden">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">Monthly Trend</div>
            <div className="border border-neutral-300 p-4 h-[280px]" data-testid="report-monthly-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.monthly.map((m) => ({ ...m, label: monthName(m.month) }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#525252", fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "#E5E5E5" }} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: "#525252", fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ background: "#0A0A0A", color: "#FFF", border: "none", borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 11 }}
                    formatter={(v) => formatUSD(v)}
                    cursor={{ fill: "#FAFAFA" }}
                  />
                  <Bar dataKey="net" fill="#0A0A0A" name="Net Comm." />
                  <Bar dataKey="expenses" fill="#7F1D1D" name="Expenses" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>

        {/* Expense detail */}
        <section className="mb-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">IV. Expense Detail</div>
          <div className="overflow-x-auto border border-neutral-300">
            <table className="w-full text-sm">
              <thead className="bg-neutral-900 text-white">
                <tr>
                  <th className="text-left font-sans font-medium uppercase tracking-[0.12em] text-[10px] px-3 py-2">Date</th>
                  <th className="text-left font-sans font-medium uppercase tracking-[0.12em] text-[10px] px-3 py-2">Category</th>
                  <th className="text-left font-sans font-medium uppercase tracking-[0.12em] text-[10px] px-3 py-2">Vendor</th>
                  <th className="text-left font-sans font-medium uppercase tracking-[0.12em] text-[10px] px-3 py-2">Notes</th>
                  <th className="text-right font-sans font-medium uppercase tracking-[0.12em] text-[10px] px-3 py-2">Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.expenses.length === 0 ? (
                  <tr><td colSpan={5} className="text-center text-neutral-500 py-6">No expenses in this period.</td></tr>
                ) : data.expenses.map((e) => (
                  <tr key={e.id} className="row-hover border-t border-neutral-200" data-testid={`report-exp-row-${e.id}`}>
                    <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{formatDate(e.expense_date)}</td>
                    <td className="px-3 py-2">{e.category}</td>
                    <td className="px-3 py-2 text-neutral-600">{e.vendor || "—"}</td>
                    <td className="px-3 py-2 text-neutral-600">{e.notes || "—"}</td>
                    <td className="px-3 py-2 font-mono text-right" style={{ color: "var(--expense)" }}>{formatUSD(e.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <div className="mt-10 pt-4 border-t border-neutral-200 text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-400 leading-relaxed">
          {theme?.footer_text || `Generated by Ledger for ${brokerageName} · Net commission and net profit are calculated from agent-entered records. Consult a licensed CPA before filing.`}
        </div>
      </div>

      {/* CPA Share Links manager (hidden in print) */}
      <div className="print:hidden mt-8">
        <ShareLinksManager
          mode={mode}
          defaultYear={year}
          defaultStart={start}
          defaultEnd={end}
        />
      </div>
      </>
      )}
    </div>
  );
}

function SummaryRow({ label, value, mono, emphasis, color, testid }) {
  return (
    <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-200 even:bg-neutral-50/50" data-testid={testid}>
      <span className={`text-sm ${emphasis ? "font-semibold text-neutral-900" : "text-neutral-700"}`}>{label}</span>
      <span
        className={`${mono ? "font-mono" : ""} ${emphasis ? "text-lg font-semibold" : "text-base"}`}
        style={color === "expense" ? { color: "var(--expense)" } : color === "profit" ? { color: "var(--profit)" } : {}}
      >
        {value}
      </span>
    </div>
  );
}
